// Small auth-ready gate used to deterministically wait until OIDC initialization
// has completed. This avoids races where API calls are issued before the
// access token has been written to sessionStorage or Redux.

let resolveReady;
let readyPromise = null;
let isReady = false;
// Debug logging disabled for minimal build
const debugLog = () => {};

const createPendingPromise = () => {
  if (!readyPromise) {
    readyPromise = new Promise((resolve) => {
      resolveReady = resolve;
    });
  }
  return readyPromise;
};

export const reset = () => {
  isReady = false;
  // Create a new pending promise immediately so any waitForAuth calls
  // that fire after reset() (e.g. on re-login) actually block until
  // markAuthReady() is called again, rather than passing through on the
  // !readyPromise fast-path and firing requests without a token.
  readyPromise = new Promise((resolve) => {
    resolveReady = resolve;
  });
  debugLog('reset');
};

export const ensurePromise = () => {
  if (isReady) {
    debugLog('ensurePromise: already ready');
    return Promise.resolve(true);
  }
  if (!readyPromise) {
    debugLog('ensurePromise: create pending promise');
  }
  return createPendingPromise();
};

export const markAuthReady = () => {
  isReady = true;
  if (resolveReady) {
    resolveReady(true);
  }
  readyPromise = null;
  resolveReady = undefined;
  debugLog('markAuthReady');
};

export const waitForAuth = async (timeoutMs = 2000) => {
  if (isReady) {
    debugLog('waitForAuth: already ready');
    return true;
  }
  if (!readyPromise) {
    debugLog('waitForAuth: no pending promise, continue');
    return true;
  }
  if (timeoutMs <= 0) {
    debugLog('waitForAuth: waiting without timeout');
    return readyPromise;
  }

  debugLog('waitForAuth: waiting with timeout', { timeoutMs });
  // Race between auth readiness and timeout
  const result = await Promise.race([
    readyPromise,
    new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs)),
  ]);
  debugLog('waitForAuth: completed', { result });
  return result;
};
